import axios from 'axios';

// Règles de saisie des formulaires d'inscription du site public.
//
// Ce sont les limites des colonnes de la base : une valeur qui les respecte
// sera acceptée par le serveur. Le serveur applique les mêmes règles
// (backend/src/services/inscription/reglesApprenant.js et telephonesUrgence.js) :
// les deux fichiers doivent rester identiques.

export const MESSAGE_ERREUR_SERVEUR =
  "Une erreur est survenue côté serveur. Veuillez contacter un administrateur pour qu'il vous inscrive manuellement : info@vitasecours.ca ou 450 300-5005.";

export const COURRIEL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Champs d'un apprenant tels que nommés dans les formulaires.
export const CHAMPS_APPRENANT = {
  nom: { libelle: 'Nom', max: 100, requis: true },
  prenom: { libelle: 'Prénom', max: 100, requis: true },
  email: { libelle: 'Courriel', max: 255, requis: true },
  telephone: { libelle: 'Téléphone', max: 20, requis: true },
  adressepostale: { libelle: 'Adresse postale', max: 255, requis: true },
  numero_appartement: { libelle: 'Appartement / bureau', max: 20 },
  ville: { libelle: 'Ville', max: 100, requis: true },
  province: { libelle: 'Province', max: 50, requis: true },
  code_postal: { libelle: 'Code postal', max: 10, requis: true },
  nom_payeur: { libelle: 'Nom du payeur', max: 100 },
  prenom_payeur: { libelle: 'Prénom du payeur', max: 100 },
  allergies: { libelle: 'Allergies', max: 1000 },
  besoins_particuliers: { libelle: 'Besoins particuliers', max: 1000 },
  ecole_frequentee: { libelle: 'École fréquentée', max: 255 },
};

// Le serveur nomme le payeur « responsable » ; le formulaire, « payeur ».
const CHAMP_DU_FORMULAIRE = { nom_responsable: 'nom_payeur', prenom_responsable: 'prenom_payeur' };

const texte = (valeur) => (valeur == null ? '' : String(valeur).trim());

/** « Nom : 100 caractères au plus (il y en a 112) », ou '' si la longueur va. */
export function erreurLongueur(valeur, max, libelle) {
  const longueur = texte(valeur).length;
  return longueur > max ? `${libelle} : ${max} caractères au plus (il y en a ${longueur})` : '';
}

/** Compteur affiché sous un champ quand on approche de sa limite : « 92 / 100 ». */
export function compteur(valeur, max) {
  const longueur = String(valeur ?? '').length;
  return longueur >= max * 0.8 ? `${longueur} / ${max}` : '';
}

// ── Téléphones d'urgence ─────────────────────────────────────────────────────

export const MAX_TELEPHONES_URGENCE = 3;
export const MAX_CARACTERES_TELEPHONE = 20;
const CARACTERES_PERMIS = /^[0-9+().\-\s]+$/;

/** Liste de numéros, quelle que soit la forme reçue (liste, JSON, numéro seul). */
export function listeTelephones(valeur) {
  if (valeur == null) return [];
  if (Array.isArray(valeur)) return valeur.map((n) => texte(n)).filter(Boolean);
  const seul = texte(valeur);
  if (!seul) return [];
  if (seul.startsWith('[')) {
    try {
      const liste = JSON.parse(seul);
      if (Array.isArray(liste)) return listeTelephones(liste);
    } catch {
      // un numéro seul qui commencerait par « [ »
    }
  }
  return [seul];
}

/** « 514 686 3885 · 438 221 5462 » pour un affichage ou un export. */
export function texteTelephones(valeur) {
  return listeTelephones(valeur).join(' · ');
}

/**
 * Inverse de texteTelephones, pour un champ texte unique de l'administration :
 * les numéros sont séparés par « · », « ; », « | », « / » entouré d'espaces
 * ou un retour à la ligne.
 */
export function decouperTelephones(texteSaisi) {
  if (Array.isArray(texteSaisi)) return listeTelephones(texteSaisi);
  return listeTelephones(String(texteSaisi ?? '').split(/[·;|\n]|\s\/\s/));
}

/** Erreur d'un seul numéro d'urgence, ou ''. Aucun format national imposé. */
export function erreurTelephone(numero, nom = "Téléphone d'urgence") {
  const valeur = texte(numero);
  if (!valeur) return '';
  if (valeur.length > MAX_CARACTERES_TELEPHONE) {
    return `${nom} : ${MAX_CARACTERES_TELEPHONE} caractères au plus (il y en a ${valeur.length}). Un seul numéro par case`;
  }
  if (!CARACTERES_PERMIS.test(valeur)) {
    return `${nom} : seuls les chiffres, espaces, +, parenthèses, tirets et points sont acceptés`;
  }
  const chiffres = valeur.replace(/\D/g, '').length;
  if (chiffres < 7 || chiffres > 15) return `${nom} : un numéro contient entre 7 et 15 chiffres`;
  return '';
}

// ── Formulaire d'un apprenant ────────────────────────────────────────────────

/**
 * Erreur d'un champ pendant la saisie : seulement ce qui est déjà faux quoi
 * qu'on tape ensuite (limite dépassée). Le champ vide ou le courriel incomplet
 * ne sont signalés qu'au passage à l'étape suivante.
 */
export function erreurPendantSaisie(champ, valeur) {
  const regle = CHAMPS_APPRENANT[champ];
  return regle ? erreurLongueur(valeur, regle.max, regle.libelle) : '';
}

/**
 * Toutes les erreurs d'un formulaire d'apprenant : { champ: message }.
 * Les téléphones d'urgence sont signalés case par case :
 * telephone_urgence_0, telephone_urgence_1…
 * `champs` limite la vérification aux champs présents dans le formulaire.
 */
export function erreursFormulaireApprenant(formData, { pourUnTiers = false, champs = Object.keys(CHAMPS_APPRENANT) } = {}) {
  const erreurs = {};

  for (const champ of champs) {
    const regle = CHAMPS_APPRENANT[champ];
    if (!regle) continue;
    const valeur = texte(formData[champ]);
    const obligatoire = regle.requis || (pourUnTiers && (champ === 'nom_payeur' || champ === 'prenom_payeur'));
    if (!valeur) {
      if (obligatoire) erreurs[champ] = `${regle.libelle} : champ obligatoire`;
      continue;
    }
    const longueur = erreurLongueur(valeur, regle.max, regle.libelle);
    if (longueur) erreurs[champ] = longueur;
    else if (champ === 'email' && !COURRIEL_VALIDE.test(valeur)) {
      erreurs[champ] = 'Courriel : adresse invalide (exemple : nom@domaine.ca)';
    }
  }

  if ('date_naissance' in formData) {
    const date = texte(formData.date_naissance);
    const annee = Number(date.slice(0, 4));
    if (!date) erreurs.date_naissance = 'Date de naissance : champ obligatoire';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || annee < 1900 || new Date(`${date}T00:00:00`) > new Date()) {
      erreurs.date_naissance = 'Date de naissance : date invalide';
    }
  }

  if (pourUnTiers) {
    const numeros = Array.isArray(formData.telephone_urgence) ? formData.telephone_urgence : [formData.telephone_urgence];
    const plusieurs = numeros.length > 1;
    numeros.forEach((numero, i) => {
      const message = erreurTelephone(numero, plusieurs ? `Téléphone d'urgence n° ${i + 1}` : "Téléphone d'urgence");
      if (message) erreurs[`telephone_urgence_${i}`] = message;
    });
    if (listeTelephones(numeros).length === 0) {
      erreurs.telephone_urgence_0 = "Téléphone d'urgence : au moins un numéro est obligatoire";
    }
  }

  return erreurs;
}

/**
 * Données d'apprenant envoyées au serveur, depuis le formulaire d'un article
 * du panier. Une seule fonction pour l'ajout au panier, la vérification et les
 * deux moyens de paiement : ils envoient exactement la même chose.
 */
export function apprenantPourServeur(formData, isForSelf) {
  const f = formData || {};
  return {
    nom: f.nom,
    prenom: f.prenom,
    email: f.email,
    telephone: f.telephone,
    adressepostale: f.adressepostale,
    numero_appartement: f.numero_appartement,
    ville: f.ville,
    province: f.province,
    code_postal: f.code_postal,
    date_naissance: texte(f.date_naissance).split('T')[0],
    accepte: true,
    telephone_urgence: isForSelf ? null : (listeTelephones(f.telephone_urgence).length ? listeTelephones(f.telephone_urgence) : null),
    allergies: f.allergies || null,
    besoins_particuliers: f.besoins_particuliers || null,
    ecole_frequentee: f.ecole_frequentee || null,
    ...(isForSelf ? {} : { nom_responsable: f.nom_payeur, prenom_responsable: f.prenom_payeur }),
  };
}

/** Article du panier au format attendu par la vérification du serveur. */
export function articlePourVerification(item) {
  return {
    apprenant: apprenantPourServeur(item.formData, item.isForSelf),
    session_id: item.sessionId,
    pour_un_tiers: !item.isForSelf,
    formationTitre: item.formationTitre,
  };
}

/**
 * Demande au serveur si ces inscriptions entreraient en base.
 * Renvoie { valide, articles } ; lève MESSAGE_ERREUR_SERVEUR si le serveur ne
 * peut pas répondre (on ne laisse alors ni ajouter au panier ni payer).
 */
export async function verifierAupresDuServeur(apiUrl, items, moyen) {
  try {
    const { data } = await axios.post(`${apiUrl}/inscriptions/verifier`, { items, moyen });
    return { valide: Boolean(data.valide), articles: data.articles || [] };
  } catch (erreur) {
    const message = erreur.response?.status === 400 && erreur.response.data?.error
      ? erreur.response.data.error
      : MESSAGE_ERREUR_SERVEUR;
    throw new Error(message);
  }
}

/**
 * Erreurs d'un article renvoyées par le serveur, rangées par champ du
 * formulaire ; `autres` regroupe celles qui ne visent aucun champ (séance
 * complète, déjà inscrit…).
 */
export function erreursServeurParChamp(article) {
  const parChamp = {};
  const autres = [];
  for (const { champ, position, message } of article?.erreurs || []) {
    const nom = champ === 'telephone_urgence'
      ? `telephone_urgence_${position ?? 0}`
      : (CHAMP_DU_FORMULAIRE[champ] || champ);
    if (nom === 'session_id' || nom === 'inscription' || !nom) autres.push(message);
    else if (!parChamp[nom]) parChamp[nom] = message;
  }
  return { parChamp, autres };
}

/**
 * Messages lisibles d'une réponse d'erreur du serveur ({ errors: [{ field,
 * message }] } ou { error }) ; MESSAGE_ERREUR_SERVEUR pour une panne.
 */
export function messagesDeReponse(erreur) {
  const reponse = erreur?.response;
  if (!reponse || reponse.status >= 500) return [MESSAGE_ERREUR_SERVEUR];
  const donnees = reponse.data || {};
  if (Array.isArray(donnees.errors) && donnees.errors.length) {
    return donnees.errors.map((e) => e.message || String(e));
  }
  return [donnees.error || donnees.message || MESSAGE_ERREUR_SERVEUR];
}

/**
 * Fait défiler l'écran jusqu'au premier champ en erreur (en rouge) et y place
 * le curseur. Attend l'affichage des messages avant de chercher.
 */
export function defilerVersPremiereErreur() {
  // Court délai : laisse passer l'affichage et les défilements automatiques
  // des formulaires au changement d'étape, pour avoir le dernier mot.
  window.setTimeout(() => {
    const cible = document.querySelector('.Mui-error, [data-erreur="true"]');
    if (!cible) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const bloc = cible.closest('.MuiFormControl-root, .MuiTextField-root, [data-erreur="true"]') || cible;
    bloc.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const champ = bloc.querySelector('input, textarea');
    if (champ) champ.focus({ preventScroll: true });
  }, 120);
}
